import React, {
  useEffect,
  useState,
} from 'react';
import {
  useSelector,
  useDispatch,
} from 'react-redux';
import {
  getStatus,
} from 'redux-resource';
import {
  useLocation,
} from 'react-router-dom';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  getUiState,
} from 'web-selectors';
import {
  decodeParam,
} from 'web-utils';

import {
  Modal,
  Spinner,
  NotFound,
  SignUpWithPurchaseForm,
} from 'web-components';
import {
  Flex,
} from 'web-styled';

import ProForm from './ProForm';
import ClientForm from './ClientForm';
import * as S from './styled';

const mapState = state => ({
  readStatus: getStatus(
    state,
    'productPlans.requests.getProductPlans.status',
    true,
  ),
  plansMap: coreSelectors.getResourceMap(
    'productPlans',
  )(state),
  namespace: getUiState('namespace')(state),
});

const SignUp = () => {
  const location = useLocation();
  const [selectedPlanId, setPlanId] = useState(null);
  const params = decodeParam(location.search);
  const dispatch = useDispatch();
  const {
    readStatus,
    plansMap,
    namespace,
  } = useSelector(mapState);

  const { symbol } = namespace;
  const isClient = params.invitation_token || params.is_pro === 'false';

  useEffect(() => {
    if (symbol === 'strongfirst') {
      dispatch(
        coreActions.requestProductPlansByNamespace({
          namespace: symbol,
          callbacks: {
            onSuccess: (res: any) => {
              if (!res.length) return;
              const actives = res.filter(p => p.active);
              const monthly = actives.filter(p => p.interval === 'month');

              if (monthly) {
                setPlanId(monthly[0].id);
              } else if (actives) {
                setPlanId(actives[0].id);
              }
            },
          },
        }),
      );
    } else if (process.env.TARGET_ENV === 'production' && !isClient) {
      window.location.href = 'https://app.gymcloud.com/login';
    }
  }, [symbol, isClient]);

  const handleSubmit = ({ agreement, ...values }, callbacks) => {
    let payload = {
      ...values,
      namespace: symbol,
      signup_source: 'desktop',
    };

    if (isClient) {
      payload = {
        ...payload,
        is_pro: false,
        is_client: true,
        invitation_token: params.invitation_token,
      };
    } else if (symbol !== 'gymcloud') {
      payload = {
        ...payload,
        is_pro: false,
        is_client: true,
      };
    } else {
      payload = {
        ...payload,
        is_pro: true,
        is_client: false,
      };
    }

    dispatch(
      coreActions.signUpRequest({
        payload,
        redirectTo: '/profile_setup',
        callbacks: callbacks,
      }),
    );
  };

  return (symbol === 'strongfirst' && !isClient) ? (
    <>
      <Modal
        title="Registration is Closed"
        isOpen={!namespace.registration}
        hideCloseButton
      >
        <S.CapSignUp>
          Thank you for signing up. Unfortunately, early registration is closed.
          Check back later on
          <a
            href="https://www.strongfirst.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            {' StrongFirst.com '}
          </a>
          or email StrongFirst at
          <a
            href="mailto:contact@strongfirst.com"
            rel="noopener noreferrer"
          >
            {' contact@strongfirst.com '}
          </a>
          to find out more.
        </S.CapSignUp>
      </Modal>
      {readStatus.succeeded && (
        <SignUpWithPurchaseForm
          item={selectedPlanId ? plansMap[selectedPlanId] : {}}
          isPlan
          namespace={namespace}
          formTitle={(
            <span>
              Start Your Free Trial
            </span>
          )}
          confirmationTitle={(
            <span>
              Thanks for signing up!
              <br />
              Your free trial has started.
            </span>
          )}
          secondButtonText="Continue"
          onMobileScreenFirst={false}
        />
      )}

      {readStatus.pending && (
        <Flex
          fillSpace
          minHeight="400px"
          align="center"
          justify="center"
        >
          <Spinner message="Loading..." />
        </Flex>
      )}

      {readStatus.failed && (
        <NotFound />
      )}
    </>
  ) : (
    <S.PageWrap>
      {isClient
        ? (
          <ClientForm
            params={params}
            onSubmit={handleSubmit}
          />
        )
        : (
          <ProForm
            params={params}
            onSubmit={handleSubmit}
          />
        )}
    </S.PageWrap>
  );
};

export default SignUp;
